import { useSnapshot } from 'valtio';

import { AuthState } from '../lib/types';
import { authState } from '../state';
import { useEffect } from 'react';
import AuthModule from '..';
import { PROACTIVE_REFRESH_INTERVAL_MS } from '../constants/auth';

let mockedAuth: AuthState | null = null;

export function setMockedAuth(auth: Partial<AuthState>) {
  mockedAuth = { ...authState, ...auth };
}

/**
 * Hook for accessing authentication state with optional proactive token refresh.
 *
 * @param alwaysFresh - When true, ensures token freshness by checking every 60 seconds.
 *                      Default: false (reactive refresh via Apollo Client is sufficient for most cases)
 *
 * ## Reactive Refresh (Default: alwaysFresh = false)
 * - GraphQL uses the BFF session cookie; no bearer header
 * - Callers that need a JWT (Help Center, RTCS) call getCurrentToken()
 *
 * ## Proactive Refresh (alwaysFresh = true)
 * Use ONLY for security-sensitive scenarios where a bridge token must stay fresh:
 * - Help Center MFE (needs a bearer for /v1/help-center/auth)
 * - Recording/compliance pages
 * - Long-running operations without API calls
 *
 * @returns Authentication state snapshot (signedIn, user, loading, token, etc.)
 *
 * @example
 * // Most components (reactive refresh via GraphQL)
 * const { user, token } = useAuth();
 *
 * @example
 * // Security-sensitive page (proactive refresh every 60s)
 * const { user, token } = useAuth(true);
 */
function useAuth(alwaysFresh = false) {
  const auth = useSnapshot(authState);

  useEffect(() => {
    if (alwaysFresh) {
      // Proactive token-bridge refresh: re-fetch /bff/token every 60 seconds
      const interval = setInterval(() => {
        AuthModule.getCurrentToken();
      }, PROACTIVE_REFRESH_INTERVAL_MS);

      return () => clearInterval(interval);
    }
  }, [alwaysFresh]);

  if (mockedAuth) {
    return mockedAuth;
  }

  return auth;
}

export default useAuth;
